import { jsPDF } from "jspdf";
import {
  buildRiskDescriptionSections,
  getDescriptionFallbackLines,
} from "../../../../shared/riskDescriptionDict";

const PAGE_WIDTH = 595.28; // A4 portrait in pt
const PAGE_MARGIN = 40;
const CONTENT_WIDTH = PAGE_WIDTH - PAGE_MARGIN * 2;
const PAGE_FOOTER_MARGIN = PAGE_MARGIN;

const riskOrder = ["fire", "flood", "earthquake"];
const PDF_FONT_FAMILY = "NotoSans";
const PDF_FONT_REGULAR_FILE = "NotoSans-Regular.ttf";
const PDF_FONT_BOLD_FILE = "NotoSans-Bold.ttf";

let cachedRegularFontBase64 = null;
let cachedBoldFontBase64 = null;

const toPercent = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "-";
  return `${Math.round(number * 100)}%`;
};

const safeText = (value, fallback = "-") => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || fallback;
  }

  if (value === null || value === undefined) return fallback;
  return String(value);
};

const arrayBufferToBase64 = (arrayBuffer) => {
  const bytes = new Uint8Array(arrayBuffer);
  let binary = "";
  const chunkSize = 0x8000;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }

  return btoa(binary);
};

async function fetchFontBase64(path) {
  const response = await fetch(path);
  if (!response.ok) {
    throw new Error(`Unable to load PDF font: ${path}`);
  }
  const buffer = await response.arrayBuffer();
  return arrayBufferToBase64(buffer);
}

const setPdfFont = (pdf, style = "normal") => {
  pdf.setFont(PDF_FONT_FAMILY, style);
};

export async function ensurePdfUnicodeFonts(pdf) {
  if (!cachedRegularFontBase64) {
    cachedRegularFontBase64 = await fetchFontBase64(
      "/fonts/NotoSans-Regular.ttf",
    );
  }
  if (!cachedBoldFontBase64) {
    cachedBoldFontBase64 = await fetchFontBase64("/fonts/NotoSans-Bold.ttf");
  }

  pdf.addFileToVFS(PDF_FONT_REGULAR_FILE, cachedRegularFontBase64);
  pdf.addFont(PDF_FONT_REGULAR_FILE, PDF_FONT_FAMILY, "normal");
  pdf.addFileToVFS(PDF_FONT_BOLD_FILE, cachedBoldFontBase64);
  pdf.addFont(PDF_FONT_BOLD_FILE, PDF_FONT_FAMILY, "bold");
}

function drawMultilineText(pdf, text, x, y, maxWidth, lineHeight = 14) {
  const lines = pdf.splitTextToSize(safeText(text), maxWidth);
  let currentY = y;

  lines.forEach((line) => {
    currentY = ensureVerticalSpace(pdf, currentY, lineHeight);
    pdf.text(line, x, currentY);
    currentY += lineHeight;
  });

  return currentY;
}

function getPageHeight(pdf) {
  return pdf.internal.pageSize.getHeight();
}

function ensureVerticalSpace(pdf, y, requiredHeight = 0) {
  const pageHeight = getPageHeight(pdf);
  const maxY = pageHeight - PAGE_FOOTER_MARGIN;
  if (y + requiredHeight > maxY) {
    pdf.addPage();
    return PAGE_MARGIN;
  }
  return y;
}

function drawPanelRows({
  pdf,
  x,
  y,
  width,
  rows,
  labelWidthRatio = 0.45,
  lineHeight = 12,
  paddingX = 6,
  paddingY = 5,
}) {
  if (!rows.length) return y;

  const labelWidth = width * labelWidthRatio;
  const valueWidth = width - labelWidth;
  const labelTextWidth = Math.max(40, labelWidth - paddingX * 2);
  const valueTextWidth = Math.max(40, valueWidth - paddingX * 2);

  const measuredRows = rows.map((row) => {
    const labelLines = pdf.splitTextToSize(safeText(row.label), labelTextWidth);
    const valueLines = pdf.splitTextToSize(safeText(row.value), valueTextWidth);
    const rowLineCount = Math.max(labelLines.length, valueLines.length, 1);
    return { labelLines, valueLines, rowLineCount };
  });
  let rowY = y;
  const pageHeight = getPageHeight(pdf);

  measuredRows.forEach((measuredRow) => {
    for (
      let lineOffset = 0;
      lineOffset < measuredRow.rowLineCount;
      lineOffset += 1
    ) {
      const maxContentHeight = pageHeight - PAGE_FOOTER_MARGIN - rowY - paddingY * 2;
      const availableLines = Math.max(1, Math.floor(maxContentHeight / lineHeight));
      const remainingLines = measuredRow.rowLineCount - lineOffset;
      const chunkLineCount = Math.max(1, Math.min(availableLines, remainingLines));
      const rowHeight = chunkLineCount * lineHeight + paddingY * 2;

      rowY = ensureVerticalSpace(pdf, rowY, rowHeight);

      const labelChunk = measuredRow.labelLines.slice(
        lineOffset,
        lineOffset + chunkLineCount,
      );
      const valueChunk = measuredRow.valueLines.slice(
        lineOffset,
        lineOffset + chunkLineCount,
      );

      pdf.setDrawColor(220, 220, 220);
      pdf.rect(x, rowY, width, rowHeight);
      pdf.line(x + labelWidth, rowY, x + labelWidth, rowY + rowHeight);

      const textY = rowY + paddingY + lineHeight - 2;
      setPdfFont(pdf, "normal");
      pdf.text(labelChunk, x + paddingX, textY);
      pdf.text(valueChunk, x + labelWidth + paddingX, textY);

      rowY += rowHeight;
      lineOffset += chunkLineCount - 1;
    }
  });

  return rowY;
}

function drawRiskDescriptionSections({ pdf, yStart, maxWidth, sections }) {
  let y = yStart;

  sections.forEach((section) => {
    if (section.title) {
      y = ensureVerticalSpace(pdf, y, 12);
      setPdfFont(pdf, "bold");
      pdf.text(section.title, PAGE_MARGIN, y);
      y += 12;
    }

    if (section.rows.length) {
      y = drawPanelRows({
        pdf,
        x: PAGE_MARGIN,
        y,
        width: maxWidth,
        rows: section.rows,
        labelWidthRatio: 0.45,
      });
      y += 16;
    }

    if (section.note) {
      setPdfFont(pdf, "normal");
      y = drawMultilineText(
        pdf,
        safeText(section.note),
        PAGE_MARGIN,
        y,
        maxWidth,
        12,
      );
      y += 10;
    }

    section.items.forEach((item) => {
      if (item.title) {
        setPdfFont(pdf, "bold");
        y = drawMultilineText(
          pdf,
          `• ${safeText(item.title)}`,
          PAGE_MARGIN,
          y,
          maxWidth,
          12,
        );
        y += 3;
      }

      if (item.rows.length) {
        y = drawPanelRows({
          pdf,
          x: PAGE_MARGIN + 10,
          y,
          width: maxWidth - 10,
          rows: item.rows,
          labelWidthRatio: 0.45,
        });
        y += 16;
      }
    });

    y += 8;
  });

  return y;
}

function drawInlineLabelValue({
  pdf,
  label,
  value,
  x,
  y,
  maxWidth,
  lineHeight = 13,
  gap = 8,
}) {
  const normalizedLabel = `${safeText(label, "")}:`;
  let currentY = ensureVerticalSpace(pdf, y, lineHeight);

  setPdfFont(pdf, "bold");
  pdf.text(normalizedLabel, x, currentY);
  const labelWidth = pdf.getTextWidth(normalizedLabel);

  const valueX = x + labelWidth + gap;
  const valueWidth = maxWidth - labelWidth - gap;
  const wrappedValue = pdf.splitTextToSize(
    safeText(value),
    valueWidth < 80 ? maxWidth : valueWidth,
  );

  setPdfFont(pdf, "normal");

  if (valueWidth < 80) {
    wrappedValue.forEach((line, index) => {
      currentY = ensureVerticalSpace(pdf, currentY + lineHeight, lineHeight);
      pdf.text(line, x, currentY);
      if (index === wrappedValue.length - 1) {
        currentY += lineHeight;
      }
    });
    return currentY;
  }

  wrappedValue.forEach((line, index) => {
    currentY = ensureVerticalSpace(pdf, currentY, lineHeight);
    pdf.text(line, valueX, currentY);
    currentY += lineHeight;
  });

  return currentY;
}

function drawHeader({
  pdf,
  logoDataUrl,
  logoWidth = 100,
  logoHeight = 43,
  title,
  generatedAtText,
}) {
  let currentY = PAGE_MARGIN;

  if (logoDataUrl) {
    pdf.addImage(logoDataUrl, "PNG", PAGE_MARGIN, currentY, logoWidth, logoHeight);
  }

  setPdfFont(pdf, "bold");
  pdf.setFontSize(16);
  pdf.text(title, PAGE_MARGIN, currentY + 64);

  setPdfFont(pdf, "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(85, 85, 85);
  pdf.text(generatedAtText, PAGE_MARGIN, currentY + 80);
  pdf.setTextColor(0, 0, 0);
}

function drawPropertySection({ pdf, labels, assessment, yStart }) {
  let y = yStart;
  setPdfFont(pdf, "bold");
  pdf.setFontSize(12);
  pdf.text(labels.propertyDetails, PAGE_MARGIN, y);
  y += 16;

  setPdfFont(pdf, "normal");
  pdf.setFontSize(10);

  const rows = [];
  if (assessment.name) rows.push([labels.name, safeText(assessment.name)]);
  rows.push([labels.address, safeText(assessment.address)]);
  rows.push([labels.floor, safeText(assessment.floor)]);
  rows.push([labels.assessmentDate, safeText(assessment.date)]);
  if (assessment.notes) rows.push([labels.notes, safeText(assessment.notes)]);

  rows.forEach(([label, value]) => {
    y = drawInlineLabelValue({
      pdf,
      label,
      value,
      x: PAGE_MARGIN,
      y,
      maxWidth: CONTENT_WIDTH,
      lineHeight: 13,
      gap: 8,
    });
    y += 4;
  });

  return y + 8;
}

function drawRiskPage({
  pdf,
  riskKey,
  labels,
  riskData,
  snapshotDataUrl,
  pageNumber,
}) {
  if (pageNumber > 1) {
    pdf.addPage();
  }

  let y = PAGE_MARGIN;
  setPdfFont(pdf, "bold");
  pdf.setFontSize(15);
  pdf.text(labels.riskTitle(riskKey), PAGE_MARGIN, y);
  y += 20;

  setPdfFont(pdf, "normal");
  pdf.setFontSize(11);

  y = drawInlineLabelValue({
    pdf,
    label: labels.riskScore,
    value: toPercent(riskData?.risk),
    x: PAGE_MARGIN,
    y,
    maxWidth: CONTENT_WIDTH,
    lineHeight: 13,
    gap: 8,
  });
  y += 3;

  y = drawInlineLabelValue({
    pdf,
    label: labels.riskMessage,
    value: safeText(riskData?.message),
    x: PAGE_MARGIN,
    y,
    maxWidth: CONTENT_WIDTH,
    lineHeight: 13,
    gap: 8,
  });
  y += 4;

  y = ensureVerticalSpace(pdf, y, 14);
  setPdfFont(pdf, "bold");
  pdf.text(`${labels.riskDescription}: `, PAGE_MARGIN, y);
  y += 14;
  const structuredSections = buildRiskDescriptionSections({
    riskType: riskKey,
    descriptionDict: riskData?.description_dict,
    risk: riskData?.risk,
    locale: labels?.descriptionDictLocale,
    translate: labels?.descriptionDictTranslate,
  });

  if (structuredSections.length) {
    y = drawRiskDescriptionSections({
      pdf,
      yStart: y,
      maxWidth: CONTENT_WIDTH,
      sections: structuredSections,
    });
  } else {
    const fallbackLines = getDescriptionFallbackLines(riskData?.description);
    setPdfFont(pdf, "normal");
    y = drawMultilineText(
      pdf,
      safeText(fallbackLines.join("\n")),
      PAGE_MARGIN,
      y,
      CONTENT_WIDTH,
      13,
    );
  }
  y += 14;

  if (snapshotDataUrl) {
    const snapshotTitleHeight = 12;
    const snapshotTopSpacing = 10;
    const snapshotHeight = 245;
    const snapshotBlockHeight =
      snapshotTitleHeight + snapshotTopSpacing + snapshotHeight;
    y = ensureVerticalSpace(pdf, y, snapshotBlockHeight);

    setPdfFont(pdf, "bold");
    pdf.text(labels.mapSnapshot, PAGE_MARGIN, y);
    y += 10;
    pdf.addImage(snapshotDataUrl, "PNG", PAGE_MARGIN, y, CONTENT_WIDTH, 245);
  }
}

export async function loadImageAsDataUrl(imageUrl) {
  if (!imageUrl) return null;
  const response = await fetch(imageUrl);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read image data."));
    reader.readAsDataURL(blob);
  });
}

export async function buildAssessmentPdf({
  assessment,
  snapshots,
  labels,
  logoDataUrl,
  logoWidth,
  logoHeight,
}) {
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  await ensurePdfUnicodeFonts(pdf);
  setPdfFont(pdf, "normal");
  drawHeader({
    pdf,
    logoDataUrl,
    logoWidth,
    logoHeight,
    title: labels.reportTitle,
    generatedAtText: labels.generatedAt,
  });

  drawPropertySection({
    pdf,
    labels,
    assessment,
    yStart: PAGE_MARGIN + 110,
  });

  riskOrder.forEach((riskKey, index) => {
    drawRiskPage({
      pdf,
      riskKey,
      labels,
      riskData: assessment?.risks?.[riskKey] || {},
      snapshotDataUrl: snapshots?.[riskKey] || null,
      pageNumber: index + 2,
    });
  });

  return pdf;
}

function sanitizeFilenamePart(value, fallback) {
  const normalized = safeText(value, fallback)
    .replace(/[\\/:*?"<>|]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return normalized || fallback;
}

export function buildPdfFilename({ address, latitude, longitude, dateText }) {
  const locationPart = sanitizeFilenamePart(
    address || `${safeText(latitude)}, ${safeText(longitude)}`,
    `${safeText(latitude)}, ${safeText(longitude)}`,
  );
  const datePart = safeText(dateText, "assessment");
  return `${locationPart} | ${datePart}.pdf`;
}
